import * as Colors from '@brightlayer-ui/colors';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { Settings } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useRef } from 'react';
import { GetUsersInOrganization } from '../../../services/organization/types';

export const styles = {
    listItem: {
        maxHeight: '50px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        '& .MuiListItemText-root': {
            flex: 0.4,
        },
        '& .MuiTypography-root.MuiTypography-body1': { fontWeight: 'normal', fontSize: '1rem' },
    },
    rightComponent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        textTransform: 'lowercase',
        justifyContent: 'space-between',
        width: '100%',
        marginLeft: '-16px',
    },
    settingsIcon: { color: Colors.gray[300] },
    userRole: { textTransform: 'capitalize' },
    menuItem: {
        maxWidth: '400px',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
    },
};

type UsersListItemProps = {
    user: GetUsersInOrganization;
    itemLabel: string;
    userMenuItems: Array<{ testid: string; text: string; onClick?: () => void; disabled?: boolean }>;
    isUserOwner: boolean;
    showUserMenu: boolean;
    setShowUserMenu: (user: GetUsersInOrganization | null) => void;

    isCredentialCreated: boolean;
};

export const UsersListItem: React.FC<UsersListItemProps> = (props) => {
    const { user, isUserOwner, showUserMenu, setShowUserMenu, userMenuItems, itemLabel, isCredentialCreated } = props;
    const userMenuAnchor = useRef<HTMLButtonElement | null>(null);

    return (
        <InfoListItem
            data-testid={`user-list-item-${user.id}`}
            sx={styles.listItem}
            title={`${user.firstName} ${user.lastName}`}
            backgroundColor={Colors.white[50]}
            hidePadding={true}
            divider={'full'}
            rightComponent={
                <Box sx={styles.rightComponent}>
                    <Typography data-testid={`org-user-role-${user.id}`} variant="body2" sx={styles.userRole}>
                        {itemLabel}
                    </Typography>
                    {isUserOwner && (
                        <>
                            <IconButton
                                data-testid={`edit-user-icon-${user.id}`}
                                ref={userMenuAnchor}
                                disabled={isCredentialCreated}
                                onClick={(e): void => {
                                    e.stopPropagation();
                                    setShowUserMenu(user);
                                }}
                            >
                                <Settings sx={styles.settingsIcon} />
                            </IconButton>
                            <Menu
                                data-testid={'edit-user-popup'}
                                transitionDuration={{ exit: 0 }}
                                open={showUserMenu}
                                onClose={(): void => setShowUserMenu(null)}
                                anchorEl={userMenuAnchor.current}
                            >
                                {userMenuItems.map((value) => (
                                    <MenuItem
                                        data-testid={`user-menu-item-${value.testid}`}
                                        key={`user-menu-item-${value.text}`}
                                        onClick={value.onClick}
                                        disabled={value.disabled ?? false}
                                        sx={styles.menuItem}
                                    >
                                        {value.text}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>
            }
            disabled={undefined}
            selected={undefined}
        />
    );
};
